import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../context/AuthContext';
import { DataContext } from '../../../context/DataContext';
import { updateActivity, uploadActivityPhotos, deleteActivityStorageFiles, uploadDocument, deleteDocument } from '../../../firebase/services';
import { compressFullSizeImage, generateThumbnail } from '../../../utils/imageUtils';
import { validateDocumentType, validateDocumentSize, formatFileSize } from '../../../utils/fileUtils';
import { parseVideoUrl, getVideoThumbnail, fetchVimeoThumbnail } from '../../../utils/videoUtils';
import { BottomNav } from '../../common/BottomNav';
import './edit-activity.css';

const EditActivityScreen = () => {
  const { t } = useTranslation();
  const { activityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { causes, updateActivityInState } = useContext(DataContext);

  const activity = location.state?.activity;

  const [title, setTitle] = useState('');
  const [reflection, setReflection] = useState('');
  const [selectedCauseId, setSelectedCauseId] = useState('');
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState(null);
  const [newPhotoFile, setNewPhotoFile] = useState(null);
  const [newPhotoPreview, setNewPhotoPreview] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [existingDocument, setExistingDocument] = useState(null);
  const [removeDocument, setRemoveDocument] = useState(false);
  const [documentError, setDocumentError] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoError, setVideoError] = useState('');
  const [videoPreview, setVideoPreview] = useState(null);
  const [documentLinks, setDocumentLinks] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Track original photo URLs for cleanup
  const [originalImageUrl] = useState(activity?.imageUrl || null);
  const [originalThumbnailUrl] = useState(activity?.thumbnailUrl || null);

  useEffect(() => {
    if (!activity) {
      // No activity data passed — go back
      navigate('/progress');
      return;
    }
    setTitle(activity.title || '');
    setReflection(activity.reflection || '');
    setCurrentPhotoUrl(activity.imageUrl || null);

    if (activity.documentUrl && activity.documentName) {
      setExistingDocument({ url: activity.documentUrl, name: activity.documentName });
    }

    if (activity.videoUrl) {
      setVideoUrl(activity.videoUrl);
      const parsed = parseVideoUrl(activity.videoUrl);
      if (parsed) {
        if (parsed.platform === 'youtube') {
          setVideoPreview({
            platform: 'youtube',
            videoId: parsed.videoId,
            thumbnailUrl: getVideoThumbnail('youtube', parsed.videoId)
          });
        } else if (parsed.platform === 'vimeo') {
          fetchVimeoThumbnail(parsed.url).then(thumb => {
            setVideoPreview({
              platform: 'vimeo',
              videoId: parsed.videoId,
              thumbnailUrl: thumb
            });
          });
        }
      }
    }

    if (activity.documentLinks) {
      setDocumentLinks(activity.documentLinks);
    }

    // Find matching cause by title
    if (causes && causes.length > 0 && activity.cause) {
      const matchingCause = causes.find(c => c.title === activity.cause.title);
      if (matchingCause) {
        setSelectedCauseId(matchingCause.docId);
      }
    }
  }, [activity, causes, navigate]);

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Photo must be under 5MB.');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    setNewPhotoFile(file);
    setNewPhotoPreview(URL.createObjectURL(file));
    setRemovePhoto(false);
    setError('');
  };

  const handleRemovePhoto = () => {
    setRemovePhoto(true);
    setNewPhotoFile(null);
    if (newPhotoPreview) {
      URL.revokeObjectURL(newPhotoPreview);
      setNewPhotoPreview(null);
    }
    setCurrentPhotoUrl(null);
  };

  const handleReplacePhoto = () => {
    document.getElementById('edit-photo-input').click();
  };

  const handleDocumentSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDocumentError('');
    if (!validateDocumentType(file)) {
      setDocumentError(t('logActivity.docTypeError'));
      return;
    }
    if (!validateDocumentSize(file)) {
      setDocumentError(t('logActivity.docSizeError'));
      return;
    }
    setDocumentFile(file);
    setRemoveDocument(true); // replacing existing doc
  };

  const handleDocumentRemove = () => {
    setDocumentFile(null);
    setExistingDocument(null);
    setRemoveDocument(true);
    setDocumentError('');
  };

  const handleVideoUrlBlur = async () => {
    if (!videoUrl.trim()) {
      setVideoPreview(null);
      setVideoError('');
      return;
    }

    const parsed = parseVideoUrl(videoUrl);
    if (!parsed) {
      setVideoPreview(null);
      setVideoError(t('logActivity.videoError'));
      return;
    }

    setVideoError('');

    if (parsed.platform === 'youtube') {
      setVideoPreview({
        platform: 'youtube',
        videoId: parsed.videoId,
        thumbnailUrl: getVideoThumbnail('youtube', parsed.videoId)
      });
    } else if (parsed.platform === 'vimeo') {
      const thumb = await fetchVimeoThumbnail(parsed.url);
      setVideoPreview({
        platform: 'vimeo',
        videoId: parsed.videoId,
        thumbnailUrl: thumb
      });
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Activity title is required.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const selectedCause = causes.find(c => c.docId === selectedCauseId);
      if (!selectedCause) {
        setError('Please select a cause.');
        setSaving(false);
        return;
      }

      const updatedFields = {
        title: title.trim(),
        reflection: reflection.trim(),
        cause: {
          title: selectedCause.title,
          level: selectedCause.level,
          icon: selectedCause.icon,
          description: selectedCause.description
        }
      };

      // Handle photo changes
      let needsOldPhotoCleanup = false;

      if (newPhotoFile) {
        // Replacing photo — upload new one
        const compressedFile = await compressFullSizeImage(newPhotoFile);
        const thumbnailFile = await generateThumbnail(newPhotoFile);
        const { fullSizeUrl, thumbnailUrl } = await uploadActivityPhotos(
          compressedFile,
          thumbnailFile,
          currentUser.uid,
          activityId
        );
        updatedFields.imageUrl = fullSizeUrl;
        updatedFields.thumbnailUrl = thumbnailUrl;
        needsOldPhotoCleanup = !!(originalImageUrl || originalThumbnailUrl);
      } else if (removePhoto) {
        // Removing photo without replacement
        updatedFields.imageUrl = null;
        updatedFields.thumbnailUrl = null;
        needsOldPhotoCleanup = !!(originalImageUrl || originalThumbnailUrl);
      }
      // If neither newPhotoFile nor removePhoto, photo fields stay unchanged (not included in update)

      // Handle document changes
      let documentUrl = existingDocument?.url || '';
      let documentName = existingDocument?.name || '';

      // Delete old document if removed or replaced
      if (removeDocument && activity.documentName) {
        await deleteDocument(currentUser.uid, activityId, activity.documentName);
        documentUrl = '';
        documentName = '';
      }

      // Upload new document if selected
      if (documentFile) {
        const docResult = await uploadDocument(currentUser.uid, activityId, documentFile);
        documentUrl = docResult.url;
        documentName = docResult.name;
      }

      updatedFields.documentUrl = documentUrl;
      updatedFields.documentName = documentName;
      updatedFields.videoUrl = videoUrl.trim() || '';
      updatedFields.documentLinks = documentLinks.trim() || '';

      await updateActivity(activityId, updatedFields);

      // Clean up old Storage files if photo was changed or removed
      if (needsOldPhotoCleanup) {
        await deleteActivityStorageFiles(
          currentUser.uid,
          activityId,
          originalImageUrl,
          originalThumbnailUrl
        ).catch(err => console.warn('Storage cleanup warning:', err));
      }

      // Update local state
      updateActivityInState(activityId, updatedFields);

      navigate('/progress');
    } catch (err) {
      console.error('Failed to update activity:', err);
      setError('Failed to save changes. Please try again.');
      setSaving(false);
    }
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (newPhotoPreview) {
        URL.revokeObjectURL(newPhotoPreview);
      }
    };
  }, [newPhotoPreview]);

  if (!activity) {
    return null;
  }

  const displayPhotoUrl = newPhotoPreview || (!removePhoto ? currentPhotoUrl : null);

  return (
    <div className="edit-activity-screen">
      <div className="edit-activity-header">
        <button className="back-arrow" onClick={() => navigate('/progress')}>
          ←
        </button>
        <h2>{t('editActivity.title')}</h2>
      </div>

      <div className="edit-activity-form">
        {/* Cause Selection */}
        <div className="form-section">
          <label className="form-label">{t('logActivity.whichCause')}</label>
          <div className="cause-options">
            {causes && causes.map(cause => (
              <label key={cause.docId} className={`cause-option ${selectedCauseId === cause.docId ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="cause"
                  value={cause.docId}
                  checked={selectedCauseId === cause.docId}
                  onChange={(e) => setSelectedCauseId(e.target.value)}
                />
                <span className="cause-option-text">{cause.title}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Activity Title */}
        <div className="form-section">
          <label className="form-label">{t('logActivity.whatDidYouDo')}</label>
          <input
            type="text"
            className="form-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Describe your activity..."
          />
        </div>

        {/* Reflection */}
        <div className="form-section">
          <label className="form-label">{t('logActivity.reflection')}</label>
          <textarea
            className="form-textarea"
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            placeholder={t('logActivity.reflectionPlaceholder')}
            rows={4}
          />
        </div>

        {/* Photo Section */}
        <div className="form-section">
          <label className="form-label">{t('logActivity.photo')}</label>

          {displayPhotoUrl ? (
            <div className="edit-photo-preview">
              <img src={displayPhotoUrl} alt="Activity" className="edit-photo-image" />
              <div className="edit-photo-actions">
                <button
                  type="button"
                  className="edit-photo-action-link"
                  onClick={handleReplacePhoto}
                >
                  {t('logActivity.replacePhoto')}
                </button>
                <button
                  type="button"
                  className="edit-photo-action-link edit-photo-remove"
                  onClick={handleRemovePhoto}
                >
                  {t('logActivity.removePhoto')}
                </button>
              </div>
            </div>
          ) : (
            <div className="edit-photo-upload" onClick={() => document.getElementById('edit-photo-input').click()}>
              <span className="edit-photo-upload-text">{t('logActivity.tapToAddPhoto')}</span>
            </div>
          )}

          <input
            id="edit-photo-input"
            type="file"
            accept="image/*"
            onChange={handlePhotoSelect}
            style={{ display: 'none' }}
          />
        </div>

        {/* Document Attachment */}
        <div style={{ marginTop: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '8px'
          }}>
            {t('logActivity.attachDocument')} {t('common.optional')}
          </label>
          <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>
            {t('logActivity.documentPrivateHelper')}
          </div>

          {/* Show existing document if present and not being replaced */}
          {existingDocument && !documentFile && (
            <div style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <span style={{ fontSize: '20px', flexShrink: 0 }}>📄</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {existingDocument.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{t('logActivity.currentDocument')}</div>
                </div>
              </div>
              <div style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <label style={{
                  fontSize: '10px',
                  color: '#999',
                  cursor: 'pointer',
                  position: 'relative',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: '400'
                }}>
                  {t('logActivity.replace')}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleDocumentSelect}
                    style={{
                      position: 'absolute',
                      top: 0, left: 0, width: '100%', height: '100%',
                      opacity: 0, cursor: 'pointer'
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={handleDocumentRemove}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '10px',
                    color: '#999',
                    cursor: 'pointer',
                    padding: 0,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '400'
                  }}
                >
                  {t('logActivity.remove')}
                </button>
              </div>
            </div>
          )}

          {/* Show new file if selected */}
          {documentFile && (
            <div style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <span style={{ fontSize: '20px', flexShrink: 0 }}>📄</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {documentFile.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {formatFileSize(documentFile.size)} — {t('logActivity.newFile')}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDocumentFile(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  color: '#999',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  flexShrink: 0
                }}
              >
                ✕
              </button>
            </div>
          )}

          {/* Show upload area if no document at all */}
          {!existingDocument && !documentFile && (
            <div style={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              padding: '16px',
              textAlign: 'center',
              cursor: 'pointer',
              position: 'relative'
            }}>
              <input
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleDocumentSelect}
                style={{
                  position: 'absolute',
                  top: 0, left: 0, width: '100%', height: '100%',
                  opacity: 0, cursor: 'pointer'
                }}
              />
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>📎</div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {t('logActivity.tapToAttachDoc')}
              </div>
              <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{t('logActivity.maxFileSize')}</div>
            </div>
          )}

          {documentError && (
            <div style={{ color: '#e74c3c', fontSize: '13px', marginTop: '6px' }}>
              {documentError}
            </div>
          )}
        </div>

        {/* Public Document Links */}
        <div style={{ marginTop: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '4px'
          }}>
            {t('logActivity.documentLinks')} {t('common.optional')}
          </label>
          <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>
            {t('logActivity.documentLinksHelper')}
          </div>
          <textarea
            value={documentLinks}
            onChange={(e) => setDocumentLinks(e.target.value)}
            placeholder={"https://example.gov/report.pdf\nhttps://research.org/study"}
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxSizing: 'border-box',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
              lineHeight: '1.5'
            }}
          />
        </div>

        {/* Video Link */}
        <div style={{ marginTop: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '8px'
          }}>
            {t('logActivity.videoLink')}
          </label>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => {
              setVideoUrl(e.target.value);
              setVideoError('');
            }}
            onBlur={handleVideoUrlBlur}
            placeholder={t('logActivity.videoLinkPlaceholder')}
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxSizing: 'border-box',
              outline: 'none'
            }}
          />
          {videoError && (
            <div style={{ color: '#e74c3c', fontSize: '13px', marginTop: '6px' }}>
              {videoError}
            </div>
          )}
          {videoPreview && videoPreview.thumbnailUrl && (
            <div style={{
              marginTop: '8px',
              position: 'relative',
              display: 'inline-block',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <img
                src={videoPreview.thumbnailUrl}
                alt="Video thumbnail"
                style={{
                  width: '160px',
                  height: '90px',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: 0,
                  height: 0,
                  borderLeft: '12px solid white',
                  borderTop: '7px solid transparent',
                  borderBottom: '7px solid transparent',
                  marginLeft: '2px'
                }} />
              </div>
            </div>
          )}
          {videoPreview && !videoPreview.thumbnailUrl && (
            <div style={{
              marginTop: '8px',
              fontSize: '13px',
              color: '#666'
            }}>
              {t('logActivity.videoThumbnailUnavailable')}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="edit-error-message">{error}</div>
        )}

        {/* Save Button */}
        <button
          className="edit-save-button"
          onClick={handleSave}
          disabled={saving || !title.trim()}
        >
          {saving ? t('editActivity.saving') : t('editActivity.saveChanges')}
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

export default EditActivityScreen;
